/**
 * Display names for every strategy the BTC 15m predictor tracks. Every name is
 * a whale or seafaring pun on how the strategy behaves. These are labels only:
 * storage keys, API fields, and code identifiers keep the original
 * `v1`…`v4`/`coinflip`/`dice` ids (see modelVersion.ts), so renaming one
 * here never touches recorded history.
 *
 * The descriptor leads wherever a strategy is labelled, so a reader sees
 * what it is before its nickname.
 */
export type StrategyKey = 'v1' | 'v1-tp' | 'v2' | 'v3' | 'v4' | 'coinflip' | 'dice';

export interface StrategyName {
  /** Full nickname, e.g. "Gone Overboard". */
  name: string;
  /** One word for tight spots such as the collapsed track-record teaser. */
  short: string;
  /** What it actually is, shown before the nickname. */
  descriptor: string;
}

export const STRATEGY_NAMES: Record<StrategyKey, StrategyName> = {
  // Hand-written rules of thumb: been there, done that.
  v1: { name: 'Baleen There', short: 'Baleen', descriptor: 'Live heuristic' },
  // Baleen There's call, but it takes the profit and leaves early.
  'v1-tp': {
    name: 'Cut and Run',
    short: 'Cut & Run',
    descriptor: 'Live heuristic + take-profit',
  },
  // A fitted regression: the pedant with the numbers.
  v2: { name: 'Whale, Actually', short: 'Actually', descriptor: 'Fitted model (R&D)' },
  // Gradient boosting: hundreds of small decisions, arguably too many.
  v3: {
    name: 'Gone Overboard',
    short: 'Overboard',
    descriptor: 'Gradient-boosted model (R&D)',
  },
  // The ensemble: conducts the three above.
  v4: {
    name: 'Orca-strated',
    short: 'Orca',
    descriptor: 'Calibrated ensemble (R&D)',
  },
  // A coin flip (a fluke is also a whale’s tail fin).
  coinflip: {
    name: 'Pure Fluke',
    short: 'Fluke',
    descriptor: 'Coin-flip baseline',
  },
  // A die roll: no purpose at all.
  dice: {
    name: 'No Porpoise',
    short: 'Porpoise',
    descriptor: 'Dice-roll baseline',
  },
};

/** Row/column heading, e.g. 'Fitted model (R&D) · Whale, Actually'. */
export function strategyLabel(key: StrategyKey): string {
  const { name, descriptor } = STRATEGY_NAMES[key];
  return `${descriptor} · ${name}`;
}
